"use client";

import { type Ref, useCallback, useEffect, useRef } from "react";
import { MentionTextarea } from "@/components/mentions";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

type RichPostComposerProps = {
  currentUser: {
    displayName: string;
    avatarUrl?: string | null;
  };
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
  maxLength?: number;
  showAvatar?: boolean;
  /** The display name above the field (hidden by X-style composers). */
  showName?: boolean;
  /**
   * Grow the field with its content instead of scrolling inside it, so
   * whatever sits under it (attachments) follows the text like a post.
   */
  autoGrow?: boolean;
  containerClassName?: string;
  bodyClassName?: string;
  textareaContainerClassName?: string;
  textareaClassName?: string;
  textareaId?: string;
  textareaRef?: Ref<HTMLTextAreaElement | null>;
};

const getInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "H";

export function RichPostComposer({
  currentUser,
  value,
  onChange,
  placeholder = "Write a caption...",
  disabled = false,
  rows = 3,
  maxLength,
  showAvatar = true,
  showName = true,
  autoGrow = false,
  containerClassName,
  bodyClassName,
  textareaContainerClassName,
  textareaClassName,
  textareaId,
  textareaRef,
}: RichPostComposerProps) {
  const fieldRef = useRef<HTMLTextAreaElement | null>(null);
  // MentionTextarea hands its node over in an effect, so forward it from here
  // to the caller's ref as well as keeping it for measuring.
  const handleFieldRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      fieldRef.current = node;
      if (!textareaRef) return;
      if (typeof textareaRef === "function") textareaRef(node);
      else textareaRef.current = node;
    },
    [textareaRef],
  );

  useEffect(() => {
    const field = fieldRef.current;
    if (!autoGrow || !field) return;
    // `value` is the trigger: re-measure on every change.
    void value;
    field.style.height = "auto";
    field.style.height = `${field.scrollHeight}px`;
  }, [autoGrow, value]);

  useEffect(() => {
    const field = fieldRef.current;
    if (!autoGrow || !field || typeof ResizeObserver === "undefined") return;
    // A width change (rotation, desktop resize) rewraps the text.
    let lastWidth = field.clientWidth;
    const observer = new ResizeObserver(() => {
      if (field.clientWidth === lastWidth) return;
      lastWidth = field.clientWidth;
      field.style.height = "auto";
      field.style.height = `${field.scrollHeight}px`;
    });
    observer.observe(field);
    return () => observer.disconnect();
  }, [autoGrow]);

  return (
    <div className={cn("flex gap-3", containerClassName)}>
      {showAvatar ? (
        <Avatar className="h-10 w-10 shrink-0 border border-border">
          <AvatarImage
            src={currentUser.avatarUrl ?? undefined}
            alt={currentUser.displayName}
          />
          <AvatarFallback>
            {getInitials(currentUser.displayName)}
          </AvatarFallback>
        </Avatar>
      ) : null}
      <div className={cn("min-w-0 flex-1", bodyClassName)}>
        {showName ? (
          <span className="text-sm font-semibold text-text-primary">
            {currentUser.displayName}
          </span>
        ) : null}
        <MentionTextarea
          id={textareaId}
          ref={handleFieldRef}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          rows={rows}
          maxLength={maxLength}
          disabled={disabled}
          containerClassName={cn(
            showName && "mt-2",
            textareaContainerClassName,
          )}
          className={cn(
            // Borderless/transparent field — drop the inherited rounded-2xl so
            // the rounded corners can't visually clip text. The base Textarea
            // paints a glass well (`dark:bg-black/35`, `dark:border-white/10`,
            // an inset `dark:shadow-*`, `backdrop-blur-md`); tailwind-merge
            // never lets an unprefixed class remove a `dark:` one, so every
            // dark twin is cleared explicitly or the field shows as a separate
            // box on the sheet in dark mode.
            "w-full resize-none appearance-none rounded-none border-none bg-transparent p-0 text-text-primary shadow-none backdrop-blur-none hover:bg-transparent focus-visible:ring-0",
            "dark:border-none dark:bg-transparent dark:shadow-none dark:hover:bg-transparent",
            autoGrow && "overflow-hidden",
            textareaClassName,
          )}
        />
      </div>
    </div>
  );
}
